import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {CSS as dndCSS} from '@dnd-kit/utilities';

import Strings from '../../../Strings';
import { PageHeader } from '../../../components/PageHeader';
import {
  COPY_DEVICE_MAPPING_FROM_GLOBAL_RESULT,
  GET_CATALOG_RESULT,
  GET_DEVICE_MAPPINGS_RESULT,
  RESET_DEVICE_MAPPINGS_RESULT,
  SET_DEVICE_MAPPINGS_RESULT,
  shippingActions,
} from '../../Shipping/actions';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { getVitalsForPermissions } from '../../../utils/readings';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { actions as orgActions } from '../../../components/OrgSelect/orgFilterActions';
import { isOrganizationManager, isShipper, isSuperUser } from '../../../utils/userRoles';
import LoadingRenderer from '../../../components/LoadingRenderer';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { saveDirtyMappingGroups } from './deviceMappingSave';
import './config.scss';

const HEART_RATE_GROUP_KEY = 'heart_rate';

// A device that the catalog no longer permits stays in the mapping list with a
// reason instead of disappearing, so an admin can see why fulfillment stopped.
const unavailableReasonText = reason => ({
  deleted: Strings.deviceUnavailableDeleted,
  not_orderable: Strings.deviceUnavailableNotOrderable,
  org_disabled: Strings.deviceUnavailableOrgDisabled,
  org_disabled_inherited: Strings.deviceUnavailableOrgDisabledInherited,
  smartmeter_unconfigured: Strings.deviceUnavailableSmartmeterUnconfigured,
  vital_not_supported: Strings.deviceUnavailableVitalNotSupported,
}[reason] || reason);

const SOURCE_ORG = 'org';
const SOURCE_INHERITED = 'inherited';

const errorMessage = (response, fallback) => response?.response?.data?.standard_error?.message || fallback;

const deviceLabel = device =>
  `${device.device_manufacturer} ${device.smartmeter_sku || device.device_model} (${device.device_type})`;

// Divergent historical mappings use the first configured member as the draft;
// saving that draft normalizes the complete group.
const deviceSetsByGroup = (mappings, vitalGroups) => {
  const byVital = {};
  mappings.forEach(m => { byVital[m.vital] = m; });

  const sets = {};
  vitalGroups.forEach(group => {
    const configured = group.vitals.map(v => byVital[v]).find(m => !!m);
    sets[group.value] = configured ? configured.device_set.map(d => d.device_uuid) : [];
  });
  return sets;
};

const mappingInfoForGroup = (group, mappingByVital) => {
  if (!group) {
    return null;
  }
  const configured = group.vitals.map(v => mappingByVital[v]).filter(Boolean);
  if (configured.length === 0) {
    return { configured: false, diverging: false };
  }
  const first = configured[0];
  const firstSet = first.device_set.map(d => d.device_uuid);
  return {
    configured: true,
    source: first.source,
    inheritedFrom: first.inherited_from_org_uuid,
    diverging: configured.length !== group.vitals.length
      || configured.some(m => m.source !== first.source
        || m.inherited_from_org_uuid !== first.inherited_from_org_uuid
        || !isEqual(m.device_set.map(d => d.device_uuid), firstSet)),
  };
};

// DnD Kit provides dynamic ARIA attributes and event handlers that must be
// attached together for keyboard and pointer sorting.
/* eslint-disable react/jsx-props-no-spreading */
function Item(props) {
  const { handleChange, id, label, checked, disabled, unavailableReason, onUp, onDown } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({id});

  const style = {
    transform: dndCSS?.Transform?.toString(transform),
    transition,
  };

  return (
    <div
      className="filter"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {onUp && onDown && (
        <button
          ref={setActivatorNodeRef}
          type="button"
          className={`drag-handle ${disabled || !checked ? 'disabled' : ''}`}
          disabled={disabled || !checked}
          {...listeners}
        />
      )}
      {onUp && <button type="button" className="arrow-button" onClick={() => onUp(id)} disabled={disabled || !checked}>⬆️</button>}
      {onDown && <button type="button" className="arrow-button" onClick={() => onDown(id)} disabled={disabled || !checked}>⬇️</button>}
      <label className={`form-control ${unavailableReason ? 'device-unavailable' : ''}`} htmlFor={id}>
        <input
          type="checkbox"
          className="filter-label"
          id={id}
          onChange={e => handleChange(id, e.target.checked)}
          checked={checked}
          disabled={disabled}
        />
        <div className="filter-text">{label}</div>
      </label>
      {/* Kept outside the label so it stays out of the checkbox accessible name. */}
      {unavailableReason && (
        <span className="device-unavailable-reason">{unavailableReasonText(unavailableReason)}</span>
      )}
    </div>
  );
}
/* eslint-enable react/jsx-props-no-spreading */
Item.propTypes = {
  id: PropTypes.string,
  handleChange: PropTypes.func,
  label: PropTypes.any,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  unavailableReason: PropTypes.string,
  onUp: PropTypes.func,
  onDown: PropTypes.func,
};

function DeviceMappings(props) {
  const {
    getDeviceMappings, getCatalog, saveDeviceMappings, resetDeviceMappings, copyDeviceMappings,
    getMyOrgs, permissions, showNotification, orgId,
  } = props;
  const [mappings, setMappings] = React.useState([]);
  const [mappingGroups, setMappingGroups] = React.useState([]);
  const [catalog, setCatalog] = React.useState([]);
  const [draft, setDraft] = React.useState({});
  const [orgsById, setOrgsById] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [divergentReset, setDivergentReset] = React.useState(null);

  const canEdit = isSuperUser() || isShipper() || isOrganizationManager();

  const vitalGroups = React.useMemo(
    () => getVitalsForPermissions(permissions, mappingGroups),
    [permissions, mappingGroups],
  );
  const [activeDeviceType, setActiveDeviceType] = React.useState(vitalGroups?.[0]?.value);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const load = React.useCallback(() => {
    setIsLoading(true);
    return Promise.all([getDeviceMappings(orgId), getCatalog(orgId)]).then(([mappingsResp, catalogResp]) => {
      const loaded = mappingsResp?.type === GET_DEVICE_MAPPINGS_RESULT ? (mappingsResp.response?.mappings || []) : [];
      const loadedGroups = mappingsResp?.type === GET_DEVICE_MAPPINGS_RESULT
        ? (mappingsResp.response?.mapping_groups || [])
        : [];
      const allowedGroups = getVitalsForPermissions(permissions, loadedGroups);
      setMappings(loaded);
      setMappingGroups(loadedGroups);
      setDraft(deviceSetsByGroup(loaded, allowedGroups));
      if (catalogResp?.type === GET_CATALOG_RESULT) {
        setCatalog(catalogResp.response?.entries || []);
      }
      setIsLoading(false);
    });
  }, [getDeviceMappings, getCatalog, orgId, permissions]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (vitalGroups.length > 0 && !vitalGroups.some(v => v.value === activeDeviceType)) {
      setActiveDeviceType(vitalGroups[0].value);
    }
  }, [vitalGroups, activeDeviceType]);

  React.useEffect(() => {
    if (!orgId) {
      return;
    }
    getMyOrgs().then(resp => {
      const orgs = Array.isArray(resp?.response?.data) ? resp.response.data : [];
      setOrgsById(orgs.reduce((acc, org) => ({ ...acc, [org.masked_id]: org }), {}));
    });
  }, [getMyOrgs, orgId]);

  const mappingByVital = React.useMemo(() => {
    const byVital = {};
    mappings.forEach(m => { byVital[m.vital] = m; });
    return byVital;
  }, [mappings]);

  const baseline = React.useMemo(() => deviceSetsByGroup(mappings, vitalGroups), [mappings, vitalGroups]);

  // Availability comes from the mapping response; the catalog only adds what a
  // device is called and its SmartMeter SKU.
  const deviceInfo = React.useMemo(() => {
    const info = {};
    catalog.forEach(d => { info[d.device_uuid] = { ...d, available: true }; });
    mappings.forEach(m => m.device_set.forEach(d => {
      info[d.device_uuid] = {
        ...(info[d.device_uuid] || {}),
        ...d,
        available: !!d.available,
        unavailable_reason: d.unavailable_reason,
      };
    }));
    return info;
  }, [catalog, mappings]);

  const selectedVital = React.useMemo(
    () => vitalGroups.find(v => v.value === activeDeviceType),
    [activeDeviceType, vitalGroups],
  );

  const selectedDevices = React.useMemo(() => {
    if (!selectedVital) {
      return [];
    }
    const mapped = draft[selectedVital.value] || [];
    const rows = mapped.map(uuid => ({ device_uuid: uuid, ...(deviceInfo[uuid] || {}), checked: true }));

    catalog
      .filter(d => selectedVital.vitals.every(v => d.measured_vitals?.includes(v)))
      .filter(d => !mapped.includes(d.device_uuid))
      .forEach(d => rows.push({ ...(deviceInfo[d.device_uuid] || d), checked: false }));

    return rows;
  }, [selectedVital, draft, catalog, deviceInfo]);

  // The badge tells the user whether this list is the org's own or someone
  // else's, because the first edit silently switches it to the org's own.
  const sourceInfo = React.useMemo(
    () => mappingInfoForGroup(selectedVital, mappingByVital),
    [selectedVital, mappingByVital],
  );

  const sourceLabel = React.useMemo(() => {
    if (!sourceInfo) {
      return null;
    }
    if (!sourceInfo.configured) {
      return Strings.deviceMappingSourceNotConfigured;
    }
    if (sourceInfo.source === SOURCE_INHERITED) {
      return Strings.formatString(
        Strings.deviceMappingSourceInherited,
        orgsById[sourceInfo.inheritedFrom]?.name || Strings.deviceMappingSourceParentOrg,
      );
    }
    if (sourceInfo.source === SOURCE_ORG && orgId) {
      return Strings.deviceMappingSourceOrg;
    }
    return Strings.deviceMappingSourceGlobal;
  }, [sourceInfo, orgsById, orgId]);

  const selectedWarning = React.useMemo(() => {
    const mapped = draft[selectedVital?.value] || [];
    if (mapped.length === 0) {
      return Strings.deviceMappingNoDeviceWarning;
    }
    return mapped.every(uuid => !deviceInfo[uuid]?.available) ? Strings.deviceMappingNoAvailableDeviceWarning : null;
  }, [draft, selectedVital, deviceInfo]);

  const dirtyGroups = React.useMemo(
    () => vitalGroups.filter(group => (
      !isEqual(draft[group.value] || [], baseline[group.value] || [])
      || mappingInfoForGroup(group, mappingByVital)?.diverging
    )),
    [vitalGroups, draft, baseline, mappingByVital],
  );

  const fulfillmentAdvisories = React.useMemo(() => {
    const advisories = [];
    for (let earlierIndex = 0; earlierIndex < vitalGroups.length; earlierIndex += 1) {
      const earlier = vitalGroups[earlierIndex];
      const earlierInfo = mappingInfoForGroup(earlier, mappingByVital);
      const earlierList = draft[earlier.value] || [];
      const earlierUuid = earlierList.find(uuid => deviceInfo[uuid]?.available);
      const earlierDevice = deviceInfo[earlierUuid];
      if (earlierInfo?.configured && earlierDevice) {
        for (let laterIndex = earlierIndex + 1; laterIndex < vitalGroups.length; laterIndex += 1) {
          const later = vitalGroups[laterIndex];
          const laterInfo = mappingInfoForGroup(later, mappingByVital);
          const laterList = draft[later.value] || [];
          const laterUuid = laterList.find(uuid => deviceInfo[uuid]?.available);
          // Heart rate is decided after every other group and covered by any ordered
          // device that measures it, listed or not: no warning into heart rate, and none
          // out of it when the later group's device covers heart rate itself.
          const laterCoversHeartRate = deviceInfo[laterUuid]?.measured_vitals?.includes('heart_rate');
          const advisoryApplies = later.key !== HEART_RATE_GROUP_KEY
            && !(earlier.key === HEART_RATE_GROUP_KEY && laterCoversHeartRate)
            && laterInfo?.configured && laterUuid
            && later.vitals.every(v => earlierDevice.measured_vitals?.includes(v))
            && !laterList.includes(earlierUuid);
          if (advisoryApplies) {
            advisories.push({
              key: `${earlier.key}-${later.key}-${earlierUuid}`,
              groupValues: [earlier.value, later.value],
              text: Strings.formatString(
                Strings.deviceMappingCrossGroupAdvisory,
                earlier.label,
                later.label,
                deviceLabel(earlierDevice),
                deviceLabel(deviceInfo[laterUuid]),
              ),
            });
          }
        }
      }
    }
    return advisories;
  }, [vitalGroups, mappingByVital, draft, deviceInfo]);

  const setSelectedDeviceOrder = React.useCallback((reorder) => {
    setDraft(prev => ({ ...prev, [selectedVital.value]: reorder(prev[selectedVital.value] || []) }));
  }, [selectedVital]);

  const handleDragEnd = event => {
    const {active, over} = event;

    if (!over || active.id === over.id) {
      return;
    }

    setSelectedDeviceOrder(mapped => {
      const oldIndex = mapped.indexOf(active.id);
      if (oldIndex < 0) {
        return mapped;
      }
      // Dropping onto an unchecked device means "least preferred": those are
      // rendered after the mapped ones.
      const overIndex = mapped.indexOf(over.id);
      return arrayMove(mapped, oldIndex, overIndex < 0 ? mapped.length - 1 : overIndex);
    });
  };

  const move = (device_uuid, offset) => {
    setSelectedDeviceOrder(mapped => {
      const index = mapped.indexOf(device_uuid);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= mapped.length) {
        return mapped;
      }
      return arrayMove(mapped, index, target);
    });
  };

  const handleChange = (device_uuid, checked) => {
    setSelectedDeviceOrder(mapped => (
      checked ? [...mapped, device_uuid] : mapped.filter(uuid => uuid !== device_uuid)
    ));
  };

  // Untouched inherited groups are omitted so opening the screen cannot detach
  // them from their parent.
  const onSave = async () => {
    setIsLoading(true);
    const failed = await saveDirtyMappingGroups({
      groups: dirtyGroups,
      draft,
      orgId,
      save: saveDeviceMappings,
      reload: load,
      successType: SET_DEVICE_MAPPINGS_RESULT,
    });

    if (failed) {
      showNotification(errorMessage(failed, Strings.deviceMappingsSaveFailed), 5000, true);
      return;
    }
    showNotification(Strings.deviceMappingsUpdated);
  };

  const onCopyFrom = async from => {
    setIsLoading(true);
    const result = await copyDeviceMappings({
      org_uuid: orgId,
      vital_types: selectedVital.vitals,
      from,
    });
    await load();

    if (result?.type !== COPY_DEVICE_MAPPING_FROM_GLOBAL_RESULT) {
      showNotification(errorMessage(result, Strings.deviceMappingsCopyFailed), 5000, true);
      return;
    }
    const divergence = result.response?.source_divergence;
    if (divergence) {
      showNotification(Strings.formatString(
        Strings.deviceMappingsCopiedDivergent,
        selectedVital.label,
        divergence.source_org_name,
      ));
      return;
    }
    // Zero added devices is not "nothing happened": the org took ownership of
    // the list either way.
    const seeded = result.response?.seeded_count || 0;
    showNotification(seeded > 0
      ? Strings.formatString(Strings.deviceMappingsCopied, seeded)
      : Strings.deviceMappingsCopiedNothingNew);
  };

  const finishReset = async result => {
    await load();

    if (result?.type !== RESET_DEVICE_MAPPINGS_RESULT) {
      showNotification(errorMessage(result, Strings.deviceMappingsResetFailed), 5000, true);
      return;
    }
    showNotification(Strings.deviceMappingsReset);
  };

  const onResetToInherited = async () => {
    setIsLoading(true);
    const params = { orgId, vital_types: selectedVital.vitals };
    const result = await resetDeviceMappings(params);
    if (result?.response?.status === 409) {
      await load();
      setDivergentReset({
        params,
        message: errorMessage(result, Strings.deviceMappingsResetFailed),
      });
      return;
    }
    await finishReset(result);
  };

  const onConfirmDivergentReset = async () => {
    const params = divergentReset?.params;
    if (!params) {
      return;
    }
    setDivergentReset(null);
    setIsLoading(true);
    const result = await resetDeviceMappings({ ...params, allow_divergent_source: true });
    await finishReset(result);
  };

  return (
    <>
      <PageHeader
        left={Strings.deviceMappings}
        right={(
          <>
            {canEdit && dirtyGroups.length > 0 && (
              <Button
                title={Strings.save}
                class={HEADER_BUTTON_DARK_BLUE}
                onClick={onSave}
                disabled={isLoading}
              />
            )}
            {canEdit && orgId && (
              <>
                {orgsById[orgId]?.parent_org_id && (
                  <Button
                    title={Strings.deviceMappingsCopyFromParent}
                    tooltip={Strings.deviceMappingsCopyFromParentTooltip}
                    class={HEADER_BUTTON_DARK_BLUE}
                    onClick={() => onCopyFrom('inherited')}
                    disabled={isLoading || !selectedVital}
                  />
                )}
                <Button
                  title={Strings.deviceMappingsCopyFromGlobal}
                  tooltip={Strings.deviceMappingsCopyFromGlobalTooltip}
                  class={HEADER_BUTTON_DARK_BLUE}
                  onClick={() => onCopyFrom('global')}
                  disabled={isLoading || !selectedVital}
                />
                {sourceInfo?.source === SOURCE_ORG && (
                  <Button
                    title={Strings.deviceMappingsResetToInherited}
                    tooltip={Strings.deviceMappingsResetToInheritedTooltip}
                    class={HEADER_BUTTON_DARK_BLUE}
                    onClick={onResetToInherited}
                    disabled={isLoading}
                  />
                )}
              </>
            )}
          </>
        )}
      />
      {divergentReset && (
        <SimpleModal
          name="device-mapping-divergent-reset-modal"
          title={Strings.deviceMappingsResetDivergentTitle}
          onCancel={() => setDivergentReset(null)}
          onSubmit={onConfirmDivergentReset}
          content={<div>{divergentReset.message}</div>}
          customButtons={(
            <div className="buttons" key="button-bar">
              <div className="bottom-button-container">
                <button
                  className="brand-white-gray"
                  type="button"
                  onClick={() => setDivergentReset(null)}
                >
                  {Strings.cancel}
                </button>
              </div>
              <div className="bottom-button-container">
                <button
                  className="remove"
                  type="button"
                  onClick={onConfirmDivergentReset}
                >
                  {Strings.deviceMappingsResetAnyway}
                </button>
              </div>
            </div>
          )}
        />
      )}
      <div style={{flex: 1, minHeight: 0}}>
        <div className="config-tab-container">
          <div className="config-modules">
            <OverlayScrollbarsComponent
              defer
              options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
              className="scrollbar-right-margin-min config-modules-nav"
            >
              <div className="config-modules-label">{Strings.modules}</div>
              <div className="tab-buttons-row config-modules-tabs">
                {vitalGroups.map(v => (
                  <button
                    key={v.value}
                    type="button"
                    className={`tabButton tab-label${activeDeviceType === v.value ? ' active-tab-label' : ''}`}
                    onClick={() => setActiveDeviceType(v.value)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </OverlayScrollbarsComponent>
            <OverlayScrollbarsComponent
              defer
              options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
              className="config-modules-content"
            >
              <LoadingRenderer loading={isLoading}>
                {activeDeviceType && selectedVital ? (
                  <div className="vital-mapping-section">
                    <div key={selectedVital.value} className="vital-mapping-section">
                      <h3>{selectedVital.label}</h3>
                      {orgId && sourceLabel && <div className="device-mapping-source">{sourceLabel}</div>}
                      {sourceInfo?.diverging && (
                        <div className="device-mapping-note">{Strings.deviceMappingDivergingVitals}</div>
                      )}
                      {selectedVital.key === HEART_RATE_GROUP_KEY && (
                        <div className="device-mapping-note">{Strings.deviceMappingHeartRateNote}</div>
                      )}
                      {fulfillmentAdvisories
                        .filter(advisory => advisory.groupValues.includes(activeDeviceType))
                        .map(advisory => (
                          <div key={advisory.key} className="device-mapping-note">{advisory.text}</div>
                        ))}
                      {selectedWarning && <div className="device-mapping-warning">{selectedWarning}</div>}
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={selectedDevices.map(d => d.device_uuid)}
                          strategy={verticalListSortingStrategy}
                        >
                          {selectedDevices.map((device) => (
                            <Item
                              key={device.device_uuid}
                              id={device.device_uuid}
                              label={deviceLabel(device)}
                              checked={device.checked}
                              unavailableReason={device.checked && !device.available ? device.unavailable_reason : undefined}
                              handleChange={handleChange}
                              disabled={!canEdit}
                              onUp={id => move(id, -1)}
                              onDown={id => move(id, 1)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                      {selectedDevices?.length === 0 && <div>{Strings.noDevicesForVital}</div>}
                    </div>
                  </div>
                ) : (
                  <div>{Strings.selectModuleToConfigure}</div>
                )}
              </LoadingRenderer>
            </OverlayScrollbarsComponent>
          </div>
        </div>
      </div>
    </>
  );
}

DeviceMappings.propTypes = {
  getDeviceMappings: PropTypes.func.isRequired,
  getCatalog: PropTypes.func.isRequired,
  saveDeviceMappings: PropTypes.func.isRequired,
  resetDeviceMappings: PropTypes.func.isRequired,
  copyDeviceMappings: PropTypes.func.isRequired,
  getMyOrgs: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  permissions: PropTypes.array,
  orgId: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    permissions: state.auth.permissions,
  };
};

const mapDispatchToProps = {
  getDeviceMappings: orgId => shippingActions.getDeviceMappings(orgId ? { orgId } : undefined),
  saveDeviceMappings: data => shippingActions.setDeviceMappings(data),
  resetDeviceMappings: params => shippingActions.resetDeviceMappings(params),
  copyDeviceMappings: data => shippingActions.copyDeviceMappingFromGlobal(data),
  getCatalog: orgId => shippingActions.getCatalog(orgId ? { orgId, orderable_only: true } : { orderable_only: true }),
  getMyOrgs: () => orgActions.getMyOrgs(),
  showNotification: (message, timeout, isError) => notificationActions.show(message, timeout, isError),
};

export default connect(mapStateToProps, mapDispatchToProps)(DeviceMappings);
